import { useState, useMemo, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listPlaces,
  createPlace,
  updatePlace,
  deletePlace,
  deletePlaces,
  checkPlaceDependencies,
  checkBulkDependencies,
  type Place,
  type PlaceCreate,
  type PlaceUpdate,
  type DependencyCheckResult,
} from '@littlebooker/dal';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ViewToggle } from '@/components/ViewToggle';
import { SearchFilter } from '@/components/SearchFilter';
import { useViewStore } from '@/lib/viewStore';
import { useAuth } from '@/contexts/AuthContext';
import { DeleteConfirmationDialog } from '@/components/DeleteConfirmationDialog';
import { DangerZone } from '@/components/DangerZone';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { PhoneInput } from '@/components/PhoneInput';
import { PlaceEquipmentManager } from '@/components/PlaceEquipmentManager';
import { PlaceStaffingManager } from '@/components/staffing/PlaceStaffingManager';
import { PhotoUploadField } from '@/components/PhotoUploadField';
import { parsePhoneNumber } from 'react-phone-number-input';
import { useDefaultCountry } from '@/hooks/useDefaultCountry';
import { Plus, Trash2, Image } from 'lucide-react';

const DEFAULT_METADATA_TEXT = '{\n  \n}';
const slugify = (value: string) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-');

// Normalize phone number: return null if empty or just country code
const normalizePhoneNumber = (phone: string): string | null => {
  if (!phone || !phone.trim()) return null;
  
  const trimmed = phone.trim();
  
  // Check if it's just a country code (pattern: + followed by 1-3 digits, nothing else)
  const countryCodePattern = /^\+\d{1,3}$/;
  if (countryCodePattern.test(trimmed)) {
    return null; // Just country code, treat as empty
  }
  
  try {
    const parsed = parsePhoneNumber(trimmed);
    // If it's a valid phone number, return the E.164 format
    if (parsed && parsed.isValid()) {
      return parsed.number;
    }
    // If parsing succeeded but number is invalid, return null
    return null;
  } catch {
    // If parsing fails completely, check if it looks like a phone number
    // If it has more than just country code, return as-is
    const hasDigits = trimmed.replace(/\D/g, '').length;
    if (hasDigits > 3) {
      // Has more than just country code digits, might be valid
      return trimmed;
    }
    return null;
  }
};


export default function PlacesPage() {
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const defaultCountry = useDefaultCountry();
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingPlace, setEditingPlace] = useState<Place | null>(null);
  const [metadataInput, setMetadataInput] = useState(DEFAULT_METADATA_TEXT);
  const [metadataError, setMetadataError] = useState<string | null>(null);
  const [createEmergencyPhone, setCreateEmergencyPhone] = useState('');
  const [editEmergencyPhone, setEditEmergencyPhone] = useState('');
  const [createLayoutImageUrl, setCreateLayoutImageUrl] = useState('');
  const [editLayoutImageUrl, setEditLayoutImageUrl] = useState('');
  const viewMode = useViewStore((state) => state.getViewMode('places'));
  const setViewMode = useViewStore((state) => state.setViewMode);
  
  // Delete functionality
  const { user } = useAuth();
  const canDelete = !user || user.role === 'owner' || user.role === 'admin';
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingPlace, setDeletingPlace] = useState<{ id: string; name: string } | null>(null);
  const [deleteDependencies, setDeleteDependencies] = useState<DependencyCheckResult | null>(null);
  const [selectedPlaceIds, setSelectedPlaceIds] = useState<Set<string>>(new Set());
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [bulkDeleteDependencies, setBulkDeleteDependencies] = useState<Map<string, DependencyCheckResult>>(new Map());
  const [checkingSingleDependencies, setCheckingSingleDependencies] = useState<Set<string>>(new Set());
  const [checkingBulkDependencies, setCheckingBulkDependencies] = useState(false);
  const startEditingPlace = (place: Place) => {
    setEditingPlace(place);
    setMetadataError(null);
    setMetadataInput(
      place.metadata && Object.keys(place.metadata).length > 0
        ? JSON.stringify(place.metadata, null, 2)
        : DEFAULT_METADATA_TEXT
    );
    setEditEmergencyPhone(place.emergency_contact_phone || '');
    setEditLayoutImageUrl(
      place.metadata && typeof place.metadata === 'object'
        ? (place.metadata as { equipment_layout_image_url?: string }).equipment_layout_image_url || ''
        : ''
    );
  };

  useEffect(() => {
    if (!editingPlace) {
      setMetadataInput(DEFAULT_METADATA_TEXT);
      setMetadataError(null);
      setEditEmergencyPhone('');
      setEditLayoutImageUrl('');
    }
  }, [editingPlace]);

  useEffect(() => {
    if (!showCreateForm) {
      setCreateEmergencyPhone('');
      setCreateLayoutImageUrl('');
    }
  }, [showCreateForm]);

  const { data: places = [], isLoading } = useQuery({
    queryKey: ['places'],
    queryFn: () => listPlaces(),
  });

  // Handle deep-link from global search - highlight the entity
  useEffect(() => {
    const state = location.state as { openEntityId?: string; entityType?: string } | null;
    if (state?.openEntityId && state?.entityType === 'place' && places.length > 0) {
      const place = places.find((p) => p.id === state.openEntityId);
      if (place) {
        // Open the edit dialog for this place
        startEditingPlace(place);
        // Clear the state so it doesn't re-trigger
        navigate(location.pathname, { replace: true, state: null });
      }
    }
  }, [location.state, places, navigate, location.pathname]);

  // Filter places based on search query
  const filteredPlaces = useMemo(() => {
    if (!searchQuery.trim()) return places;
    const lowerQuery = searchQuery.toLowerCase();
    return places.filter(
      (place) =>
        place.name.toLowerCase().includes(lowerQuery) ||
        place.slug.toLowerCase().includes(lowerQuery) ||
        place.type.toLowerCase().includes(lowerQuery) ||
        (place.address && place.address.toLowerCase().includes(lowerQuery)) ||
        (place.emergency_contact_phone && place.emergency_contact_phone.toLowerCase().includes(lowerQuery))
    );
  }, [places, searchQuery]);

  const createMutation = useMutation({
    mutationFn: (data: PlaceCreate) => createPlace(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['places'] });
      setShowCreateForm(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: PlaceUpdate }) => updatePlace(id, patch),
    onSuccess: (updatedPlace) => {
      console.log('Update mutation success, received place:', updatedPlace);
      console.log('Emergency contact phone in response:', updatedPlace.emergency_contact_phone);
      queryClient.setQueryData(['places'], (existing: Place[] | undefined) => {
        if (!existing) return existing;
        const updated = existing.map((place) => (place.id === updatedPlace.id ? updatedPlace : place));
        console.log('Updated places cache, place with phone:', updated.find(p => p.id === updatedPlace.id));
        return updated;
      });
      queryClient.invalidateQueries({ queryKey: ['places'] });
      queryClient.invalidateQueries({ queryKey: ['happenings'] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      setEditingPlace(null);
    },
  });

  // Delete mutations
  const deleteMutation = useMutation({
    mutationFn: (id: string) => deletePlace(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['places'] });
      queryClient.invalidateQueries({ queryKey: ['happenings'] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      setDeleteDialogOpen(false);
      setDeletingPlace(null);
      setDeleteDependencies(null);
      setEditingPlace(null);
    },
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: (ids: string[]) => deletePlaces(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['places'] });
      queryClient.invalidateQueries({ queryKey: ['happenings'] });
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      setBulkDeleteDialogOpen(false);
      setSelectedPlaceIds(new Set());
      setBulkDeleteDependencies(new Map());
    },
  });

  // Delete handlers
  const handleDeleteClick = async (place: { id: string; name: string }, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    if (!canDelete) return;
    
    setCheckingSingleDependencies((prev) => new Set(prev).add(place.id));
    try {
      setDeletingPlace(place);
      const dependencies = await checkPlaceDependencies(place.id);
      setDeleteDependencies(dependencies);
      setDeleteDialogOpen(true);
    } finally {
      setCheckingSingleDependencies((prev) => {
        const next = new Set(prev);
        next.delete(place.id);
        return next;
      });
    }
  };

  const handleDeleteConfirm = () => {
    if (deletingPlace) {
      deleteMutation.mutate(deletingPlace.id);
    }
  };

  const handleBulkDeleteClick = async () => {
    if (selectedPlaceIds.size === 0 || !canDelete) return;
    
    setCheckingBulkDependencies(true);
    try {
      const ids = Array.from(selectedPlaceIds);
      const dependencies = await checkBulkDependencies('place', ids);
      setBulkDeleteDependencies(dependencies);
      setBulkDeleteDialogOpen(true);
    } finally {
      setCheckingBulkDependencies(false);
    }
  };

  const handleBulkDeleteConfirm = () => {
    if (selectedPlaceIds.size > 0) {
      bulkDeleteMutation.mutate(Array.from(selectedPlaceIds));
    }
  };

  const togglePlaceSelection = (placeId: string, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    setSelectedPlaceIds((prev) => {
      const next = new Set(prev);
      if (next.has(placeId)) {
        next.delete(placeId);
      } else {
        next.add(placeId);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedPlaceIds.size === filteredPlaces.length) {
      setSelectedPlaceIds(new Set());
    } else {
      setSelectedPlaceIds(new Set(filteredPlaces.map((p) => p.id)));
    }
  };

  const handlePlaceEditSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!editingPlace) return;

    const formData = new FormData(event.currentTarget);
    const name = (formData.get('name') as string).trim();
    const slugInput = (formData.get('slug') as string).trim();
    const type = (formData.get('type') as string).trim();
    const parentIdRaw = (formData.get('parent_id') as string) ?? '';
    const address = (formData.get('address') as string)?.trim() || null;
    const metadataRaw = (formData.get('metadata') as string) ?? '';
    const isActive = formData.get('is_active') === 'on';

    const slug = slugInput.length > 0 ? slugInput : slugify(name);
    const parent_id = parentIdRaw === '' ? null : parentIdRaw;
    let metadata: Record<string, unknown> | undefined;

    if (metadataRaw.trim().length > 0) {
      try {
        metadata = JSON.parse(metadataRaw);
        setMetadataError(null);
      } catch (error) {
        setMetadataError('Metadata must be valid JSON.');
        return;
      }
    } else {
      metadata = {};
      setMetadataError(null);
    }

    const layoutUrl = editLayoutImageUrl.trim();
    if (layoutUrl) {
      metadata.equipment_layout_image_url = layoutUrl;
    } else {
      delete metadata.equipment_layout_image_url;
    }

    const normalizedPhone = normalizePhoneNumber(editEmergencyPhone);
    console.log('Updating place:', {
      id: editingPlace.id,
      emergency_contact_phone: normalizedPhone,
      rawInput: editEmergencyPhone,
      normalizedResult: normalizedPhone
    });
    
    const updateData: PlaceUpdate = {
      name,
      slug,
      type: type || undefined,
      parent_id,
      address: address || null,
      emergency_contact_phone: normalizedPhone,
      is_active: isActive,
      metadata,
    };
    
    console.log('Full update payload:', updateData);
    
    updateMutation.mutate({
      id: editingPlace.id,
      patch: updateData,
    });
  };

  const handleCreate = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const name = (formData.get('name') as string).trim();
    const slugInput = (formData.get('slug') as string).trim();
    const type = (formData.get('type') as string).trim();
    const parentIdRaw = (formData.get('parent_id') as string) ?? '';
    const address = (formData.get('address') as string)?.trim() || null;
    const metadataRaw = (formData.get('metadata') as string) ?? '';
    const isActive = formData.get('is_active') === 'on';

    const slug = slugInput.length > 0 ? slugInput : slugify(name);
    const parent_id = parentIdRaw === '' ? null : parentIdRaw;
    let metadata: Record<string, unknown> = {};

    if (metadataRaw.trim().length > 0) {
      try {
        metadata = JSON.parse(metadataRaw);
      } catch (error) {
        return;
      }
    }

    const layoutUrl = createLayoutImageUrl.trim();
    if (layoutUrl) {
      metadata.equipment_layout_image_url = layoutUrl;
    }

    const normalizedPhone = normalizePhoneNumber(createEmergencyPhone);
    console.log('Creating place:', {
      emergency_contact_phone: normalizedPhone,
      rawInput: createEmergencyPhone,
      normalizedResult: normalizedPhone
    });
    
    const createData: PlaceCreate = {
      name,
      slug,
      type: type || 'room',
      parent_id,
      address: address || null,
      emergency_contact_phone: normalizedPhone,
      is_active: isActive,
      metadata,
    };
    
    console.log('Full create payload:', createData);
    
    createMutation.mutate(createData);
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Places</h1>
          <p className="text-muted-foreground">Manage rooms, stations, stages, and venues</p>
        </div>
        <Button onClick={() => setShowCreateForm(!showCreateForm)}>
          <Plus className="mr-2 h-4 w-4" />
          Add Place
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <SearchFilter
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search places by name, slug, or type..."
          className="w-full max-w-sm"
        />
        <ViewToggle
          view={viewMode}
          onViewChange={(mode) => setViewMode('places', mode)}
          availableViews={['list']}
        />
        {searchQuery && (
          <span className="text-sm text-muted-foreground">
            {filteredPlaces.length} of {places.length} places
          </span>
        )}
      </div>

      {/* Bulk actions bar */}
      {selectedPlaceIds.size > 0 && canDelete && (
        <div className="flex items-center justify-between rounded-lg border bg-muted/50 p-3">
          <span className="text-sm font-medium">
            {selectedPlaceIds.size} place{selectedPlaceIds.size !== 1 ? 's' : ''} selected
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSelectedPlaceIds(new Set())}
              disabled={checkingBulkDependencies}
            >
              Clear selection
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={handleBulkDeleteClick}
              disabled={checkingBulkDependencies}
            >
              {checkingBulkDependencies ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  Checking dependencies...
                </>
              ) : (
                <>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete {selectedPlaceIds.size}
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {showCreateForm && (
        <Card>
          <CardHeader>
            <CardTitle>Create New Place</CardTitle>
            <CardDescription>Add a new room, station, stage, or venue</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="text-sm font-medium">Name</label>
                  <input
                    name="name"
                    required
                    className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2"
                    placeholder="Studio A"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Slug</label>
                  <input
                    name="slug"
                    className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2"
                    placeholder="studio-a"
                  />
                  <p className="mt-1 text-xs text-muted-foreground">Auto-generated if empty</p>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="text-sm font-medium">Type</label>
                  <input
                    name="type"
                    className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2"
                    placeholder="room, station, stage, venue..."
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Parent Place</label>
                  <select
                    name="parent_id"
                    className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2"
                  >
                    <option value="">None (top-level)</option>
                    {places
                      .filter((p) => p.id !== editingPlace?.id)
                      .map((place) => (
                        <option key={place.id} value={place.id}>
                          {place.name}
                        </option>
                      ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium">Address</label>
                <input
                  name="address"
                  className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2"
                  placeholder="Street address, venue location..."
                />
              </div>
              <div>
                <label className="text-sm font-medium">Emergency (On-Day) Contact Phone</label>
                <div className="mt-1">
                  <PhoneInput
                    value={createEmergencyPhone}
                    onChange={setCreateEmergencyPhone}
                    placeholder="Enter emergency contact phone number"
                    defaultCountry={defaultCountry}
                  />
                </div>
                <p className="mt-1 text-xs text-muted-foreground">Contact number for on-day emergencies</p>
              </div>
              <div className="space-y-2 rounded-md border border-input bg-muted/30 p-3">
                <div className="flex items-center gap-2">
                  <Image className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">Equipment layout / stage plot</p>
                    <p className="text-xs text-muted-foreground">Upload a diagram for this location (shown to artists).</p>
                  </div>
                </div>
                <PhotoUploadField
                  label="Upload layout image"
                  helper="Optional jpg/png"
                  value={createLayoutImageUrl}
                  placeholder="https://"
                  onChange={setCreateLayoutImageUrl}
                  category="place_layout"
                  identifier="place-layout"
                  allowBypassSizeLimit
                />
              </div>
              <div>
                <label className="flex items-center gap-2">
                  <input
                    id="place-active-create"
                    name="is_active"
                    type="checkbox"
                    defaultChecked={true}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  <span className="text-sm font-medium">Active</span>
                </label>
              </div>
              <div>
                <label className="text-sm font-medium">Metadata (JSON)</label>
                <textarea
                  name="metadata"
                  className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-sm"
                  rows={4}
                  placeholder={DEFAULT_METADATA_TEXT}
                />
                <p className="mt-1 text-xs text-muted-foreground">Optional JSON metadata</p>
              </div>
              <div className="flex gap-2">
                <Button type="submit" disabled={createMutation.isPending}>
                  Create
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowCreateForm(false)}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {isLoading ? (
        <p>Loading...</p>
      ) : viewMode === 'card' ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredPlaces.map((place) => (
            <Card
              key={place.id}
              className="relative cursor-pointer"
              onClick={() => startEditingPlace(place)}
            >
              {canDelete && (
                <div className="absolute top-2 right-2 z-10 flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={selectedPlaceIds.has(place.id)}
                    onChange={() => togglePlaceSelection(place.id)}
                    onClick={(e) => e.stopPropagation()}
                    className="h-4 w-4 rounded border-gray-300"
                    disabled={checkingSingleDependencies.has(place.id)}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => handleDeleteClick(place, e)}
                    className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                    disabled={checkingSingleDependencies.has(place.id)}
                    title={checkingSingleDependencies.has(place.id) ? 'Checking dependencies...' : 'Delete'}
                  >
                    {checkingSingleDependencies.has(place.id) ? (
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              )}
              <CardHeader>
                <CardTitle>{place.name}</CardTitle>
                <CardDescription>{place.type} • {place.slug}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {place.address && (
                  <div className="text-sm">
                    <span className="font-medium">Address: </span>
                    <span className="text-muted-foreground">{place.address}</span>
                  </div>
                )}
                {place.emergency_contact_phone && (
                  <div className="text-sm">
                    <span className="font-medium">Emergency Contact: </span>
                    <span className="text-muted-foreground">{place.emergency_contact_phone}</span>
                  </div>
                )}
                {place.metadata && Object.keys(place.metadata).length > 0 && (
                  <div className="text-sm text-muted-foreground">
                    {JSON.stringify(place.metadata, null, 2)}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                {canDelete && (
                  <TableHead className="w-12">
                    <input
                      type="checkbox"
                      checked={selectedPlaceIds.size === filteredPlaces.length && filteredPlaces.length > 0}
                      onChange={toggleSelectAll}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                  </TableHead>
                )}
                <TableHead>Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead>Address</TableHead>
                <TableHead>Emergency Contact</TableHead>
                <TableHead>Metadata</TableHead>
                {canDelete && <TableHead className="w-12">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredPlaces.map((place) => (
                <TableRow
                  key={place.id}
                  className="cursor-pointer"
                  onClick={() => startEditingPlace(place)}
                >
                  {canDelete && (
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedPlaceIds.has(place.id)}
                        onChange={() => togglePlaceSelection(place.id)}
                        className="h-4 w-4 rounded border-gray-300"
                      />
                    </TableCell>
                  )}
                  <TableCell className="font-medium">{place.name}</TableCell>
                  <TableCell>
                    <span className="rounded bg-secondary text-secondary-foreground px-2 py-0.5 text-xs font-medium capitalize">
                      {place.type}
                    </span>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{place.slug}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {place.address || '—'}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {place.emergency_contact_phone || '—'}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {place.metadata && Object.keys(place.metadata).length > 0
                      ? `${Object.keys(place.metadata).length} fields`
                      : '—'}
                  </TableCell>
                  {canDelete && (
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => handleDeleteClick(place, e)}
                        className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                        disabled={checkingSingleDependencies.has(place.id)}
                        title={checkingSingleDependencies.has(place.id) ? 'Checking dependencies...' : 'Delete'}
                      >
                        {checkingSingleDependencies.has(place.id) ? (
                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Edit Place Dialog */}
      <Dialog open={!!editingPlace} onOpenChange={(open) => !open && setEditingPlace(null)}>
        <DialogContent size="large" className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Place</DialogTitle>
            <DialogDescription>Update place details and manage equipment.</DialogDescription>
          </DialogHeader>
          {editingPlace && (
            <Tabs defaultValue="details" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="details">Details</TabsTrigger>
                <TabsTrigger value="equipment">Equipment</TabsTrigger>
                <TabsTrigger value="staffing">Staffing</TabsTrigger>
              </TabsList>

              <TabsContent value="details">
            <form key={editingPlace.id} onSubmit={handlePlaceEditSubmit} className="space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Name</label>
                  <input
                    name="name"
                    defaultValue={editingPlace.name}
                    className="w-full rounded-md border border-input bg-background px-3 py-2"
                    required
                    onChange={(e) => {
                      const slugField = e.currentTarget.form?.elements.namedItem('slug') as HTMLInputElement | null;
                      if (slugField && slugField.value === editingPlace.slug) {
                        slugField.value = slugify(e.target.value);
                      }
                    }}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Slug</label>
                  <input
                    name="slug"
                    defaultValue={editingPlace.slug}
                    className="w-full rounded-md border border-input bg-background px-3 py-2"
                    required
                  />
                  <p className="text-xs text-muted-foreground">Lowercase, URL-safe identifier.</p>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Type</label>
                  <input
                    name="type"
                    defaultValue={editingPlace.type}
                    className="w-full rounded-md border border-input bg-background px-3 py-2"
                    placeholder="room, stage, station..."
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Parent Place</label>
                  <select
                    name="parent_id"
                    defaultValue={editingPlace.parent_id ?? ''}
                    className="w-full rounded-md border border-input bg-background px-3 py-2"
                  >
                    <option value="">No parent</option>
                    {places
                      .filter((p) => p.id !== editingPlace.id)
                      .map((parent) => (
                        <option key={parent.id} value={parent.id}>
                          {parent.name}
                        </option>
                      ))}
                  </select>
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Address</label>
                <input
                  name="address"
                  defaultValue={editingPlace.address || ''}
                  className="w-full rounded-md border border-input bg-background px-3 py-2"
                  placeholder="Street address, venue location..."
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Emergency (On-Day) Contact Phone</label>
                <PhoneInput
                  value={editEmergencyPhone}
                  onChange={setEditEmergencyPhone}
                  placeholder="Enter emergency contact phone number"
                  defaultCountry={defaultCountry}
                />
                <p className="text-xs text-muted-foreground">Contact number for on-day emergencies</p>
              </div>
              <div className="space-y-2 rounded-md border border-input bg-muted/30 p-3">
                <div className="flex items-center gap-2">
                  <Image className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">Equipment layout / stage plot</p>
                    <p className="text-xs text-muted-foreground">Upload a diagram for this location (shown to artists).</p>
                  </div>
                </div>
                <PhotoUploadField
                  label="Upload layout image"
                  helper="Optional jpg/png"
                  value={editLayoutImageUrl}
                  placeholder="https://"
                  onChange={setEditLayoutImageUrl}
                  category="place_layout"
                  identifier={`place-layout-${editingPlace.id}`}
                  allowBypassSizeLimit
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  id="place-active"
                  name="is_active"
                  type="checkbox"
                  defaultChecked={editingPlace.is_active}
                  className="h-4 w-4 rounded border-input"
                />
                <label htmlFor="place-active" className="text-sm">
                  Active
                </label>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Metadata (JSON)</label>
                <textarea
                  name="metadata"
                  value={metadataInput}
                  onChange={(e) => setMetadataInput(e.target.value)}
                  className="min-h-[160px] w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-sm"
                />
                <p className="text-xs text-muted-foreground">
                  Store any structured data for this place. Leave blank for empty metadata.
                </p>
                {metadataError && <p className="text-xs text-destructive">{metadataError}</p>}
              </div>
              {canDelete && (
                <DangerZone
                  entityLabel="place"
                  onDelete={() => handleDeleteClick({ id: editingPlace.id, name: editingPlace.name })}
                  disabled={deleteMutation.isPending}
                />
              )}
              <DialogFooter className="gap-2 sm:justify-end">
                <Button type="button" variant="outline" onClick={() => setEditingPlace(null)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={updateMutation.isPending}>
                  {updateMutation.isPending ? 'Saving...' : 'Save changes'}
                </Button>
              </DialogFooter>
            </form>
              </TabsContent>

              <TabsContent value="equipment">
                <PlaceEquipmentManager
                  placeId={editingPlace.id}
                  placeName={editingPlace.name}
                />
              </TabsContent>

              <TabsContent value="staffing">
                <PlaceStaffingManager placeId={editingPlace.id} />
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      {deletingPlace && (
        <DeleteConfirmationDialog
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          onConfirm={handleDeleteConfirm}
          entityName={deletingPlace.name}
          entityType="place"
          dependencies={deleteDependencies || undefined}
          isDeleting={deleteMutation.isPending}
        />
      )}

      {/* Bulk Delete Confirmation Dialog */}
      {selectedPlaceIds.size > 0 && (
        <DeleteConfirmationDialog
          open={bulkDeleteDialogOpen}
          onOpenChange={setBulkDeleteDialogOpen}
          onConfirm={handleBulkDeleteConfirm}
          entityName=""
          entityType="place"
          entityCount={selectedPlaceIds.size}
          dependencies={(() => {
            // Aggregate dependencies from all selected places
            const allDeps: DependencyCheckResult = {
              hasDependencies: false,
              dependencies: [],
            };
            const depMap = new Map<string, number>();
            const depItems = new Map<string, Array<{ id: string; name: string }>>();

            bulkDeleteDependencies.forEach((result) => {
              if (result.hasDependencies) {
                allDeps.hasDependencies = true;
                result.dependencies.forEach((dep) => {
                  const key = dep.type;
                  depMap.set(key, (depMap.get(key) || 0) + dep.count);
                  if (!depItems.has(key)) {
                    depItems.set(key, []);
                  }
                  depItems.get(key)!.push(...dep.items);
                });
              }
            });

            depMap.forEach((count, type) => {
              allDeps.dependencies.push({
                type,
                count,
                items: depItems.get(type) || [],
              });
            });

            return allDeps;
          })()}
          isDeleting={bulkDeleteMutation.isPending}
        />
      )}
    </div>
  );
}
